import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./HeaderSection.module.scss";
import { EditIcon, ShareIcon } from "@/frontend/shared/assets/icons";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { LinkedInLettersIcon } from "@/frontend/shared/assets/icons/LinkedInLettersIcon";
import { Dispatch, SetStateAction, useState } from "react";
import clsx from "clsx";
import { refactorWebsiteLink } from "@/frontend/shared/lib/refactorWebsiteLink/refactorWebsiteLink";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { WebIcon } from "@/frontend/shared/assets/icons/WebIcon";
import { formatDateMonthYear } from "@/frontend/shared/lib/formatDateMonthYear/formatDateMonthYear";
import { postLatestPublish } from "../../model/services/postLatestPublish";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TagTabV2 } from "@/frontend/shared/ui/TagTabV2";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { EditProfileModal } from "@/frontend/entities/Founder/ui/EditProfileModal/EditProfileModal";
import { ManageAccessModal } from "@/frontend/entities/Founder/ui/ManageAccessModal/ManageAccessModal";
import { SquareNormalButton } from "@/frontend/shared/ui/Button/SquareNormalButton/SquareNormalButton";
import { EActiveView, TCompanyDetails } from "@/frontend/features/Company/model/types/types";

export function HeaderSection({
  data,
  activeView,
  onChangeActiveView,
  triggerRefetch,
  isContainerScrolled
}: {
  data?: TCompanyDetails;
  activeView: EActiveView;
  onChangeActiveView: Dispatch<SetStateAction<EActiveView>>;
  triggerRefetch: () => void;
  isContainerScrolled?: boolean;
}) {
  if (!data) return null;

  const { name, website, logoUrl, headline, linkedInUrl, lastPublishedAt, canBePublished } = data;

  const [isEditProfileModalShown, setIsEditProfileModalShown] = useState(false);
  const [isManageAccessModalShown, setIsManageAccessModalShown] = useState(false);
  const [isPublishLoading, setIsPublishLoading] = useState(false);

  async function onClickPublish() {
    try {
      setIsPublishLoading(true);
      await postLatestPublish();
      triggerRefetch();
      snackbarConstructor("Updated version of company has been published", "normal");
    } catch (e) {
      console.log(e);
      reportErrorToSentry(e as AxiosError, "FOUNDER PROFILE PAGE: /access-controls/publish");
      snackbarConstructor("Error publishing", "error");
    } finally {
      setIsPublishLoading(false);
    }
  }

  function displayedWebsiteField() {
    const content = website ? (
      <div className={styles.websiteContainerBlock}>
        <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
          <a href={refactorWebsiteLink(website)} target="_blank" rel="noreferrer" className={styles.websiteContainer}>
            <WebIcon />
            <Typography variant="label-5" color="greyscale700">
              {website}
            </Typography>
          </a>
        </NormalButton>
      </div>
    ) : null;

    return content;
  }

  const isMobile = useScreenWidth() <= 768;
  const inviteContainer = (
    <div className={styles.inviteContainer}>
      <SquareNormalButton
        onClick={() => setIsEditProfileModalShown(true)}
        variant="secondary"
        size="large"
        className={styles.editProfileButton}
      >
        <EditIcon />
        {!isMobile && "Edit Profile"}
      </SquareNormalButton>
      <NormalButton
        variant="secondary"
        size="large"
        className={styles.editProfileButton}
        onClick={() => setIsManageAccessModalShown(true)}
      >
        <ShareIcon />
        Share
      </NormalButton>
      <NormalButton
        className={styles.publishButton}
        size="large"
        isSubmitting={isPublishLoading}
        onClick={onClickPublish}
        isDisabled={!canBePublished}
      >
        Publish
      </NormalButton>
    </div>
  );
  return (
    <div className={clsx(styles.container, activeView === EActiveView.DATA_ROOM && styles.notSticky)}>
      <div className={styles.block}>
        <div className={clsx(styles.upperContent, !isContainerScrolled && styles.minimizedUpperContent)}>
          <div className={styles.logoContainer}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={name}
                className={clsx(styles.logo, !isContainerScrolled && styles.minimizedLogo)}
              />
            ) : (
              <div className={clsx(styles.logo, !isContainerScrolled && styles.minimizedLogo)} />
            )}
            {isMobile && inviteContainer}
          </div>

          <div className={styles.infoContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>
                <Typography variant="label-1" color="greyscale900">
                  {name}
                </Typography>
              </div>
              <div className={styles.info}>
                {displayedWebsiteField()}
                <div className={styles.iconsContainer}>
                  {linkedInUrl && (
                    <a href={linkedInUrl} target="_blank" rel="noreferrer" className={styles.linkedinIconContainer}>
                      <LinkedInLettersIcon className={styles.linkedinIcon} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div
              className={clsx(styles.descriptionBlock, !isContainerScrolled && isMobile && styles.hiddenDescription)}
            >
              <div className={styles.subDescription}>
                <Typography variant="body-2" color="greyscale800" className={styles.shortDescription}>
                  {headline}
                </Typography>
              </div>
            </div>
          </div>
          {!isMobile && inviteContainer}
        </div>
        <div className={clsx(styles.tagsContainerBlock, !isContainerScrolled && styles.hiddenTagsContainer)}>
          {!!data?.companyData?.harmonicTags && data?.companyData?.harmonicTags?.length > 0 && (
            <div className={styles.tagsContainer}>
              <TagTabV2 customTags={false}>
                {data?.companyData?.harmonicTags?.map((tag: string, index: number) => {
                  return <Tag children={tag} type="innovera" key={index} className={styles.tag} />;
                })}
              </TagTabV2>
            </div>
          )}
        </div>
        <div className={styles.viewSwitcherContainer}>
          <div className={styles.viewSwitcherLeftSide}>
            <Typography
              variant="label-3"
              color="greyscale400"
              onClick={() => onChangeActiveView(EActiveView.PROFILE)}
              className={clsx(styles.option, activeView === EActiveView.PROFILE && styles.active)}
            >
              Profile
            </Typography>
            <Typography
              variant="label-3"
              color="greyscale400"
              onClick={() => onChangeActiveView(EActiveView.DATA_ROOM)}
              className={clsx(styles.option, activeView === EActiveView.DATA_ROOM && styles.active)}
            >
              Data Room
            </Typography>
          </div>
          {lastPublishedAt && (
            <div className={styles.viewSwitcherRightSide}>
              <Typography variant="body-3" color="greyscale500">
                {formatDateMonthYear(lastPublishedAt)}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <EditProfileModal
        data={data}
        isOpen={isEditProfileModalShown}
        onClose={() => setIsEditProfileModalShown(false)}
        triggerRefetch={triggerRefetch}
      />
      <ManageAccessModal isOpen={isManageAccessModalShown} onClose={() => setIsManageAccessModalShown(false)} />
    </div>
  );
}
